import { Loader2, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  SearchAccountResult,
  SearchResponse,
  SearchTransactionResult,
} from "@finance/shared";

import { Input } from "@/components/ui/input";
import { globalSearch } from "@/lib/api";
import { cn } from "@/lib/utils";

const DEBOUNCE_MS = 250;
const MIN_QUERY_LEN = 2;

// FIN-47: top-nav global search. Calls `GET /search` on a 250ms debounce
// once the trimmed query is at least 2 chars and renders the grouped
// accounts + transactions response below the input. Closes on Esc, on
// click-outside, and on result selection.
export function GlobalSearch() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const trimmed = value.trim();
  const shouldQuery = trimmed.length >= MIN_QUERY_LEN;

  // Debounced fetch. The AbortController is keyed on the trimmed query so
  // a fast typist's earlier in-flight requests get cancelled instead of
  // racing back and overwriting newer results.
  useEffect(() => {
    if (!shouldQuery) {
      setResults(null);
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    const t = window.setTimeout(() => {
      globalSearch(trimmed, controller.signal)
        .then((res) => {
          setResults(res);
          setError(null);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setResults(null);
          setError(err instanceof Error ? err.message : "Search failed");
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      controller.abort();
      window.clearTimeout(t);
    };
  }, [trimmed, shouldQuery]);

  // Click-outside closes the dropdown without clearing the input — the
  // user can re-focus and reopen with the same query.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const root = containerRef.current;
      if (!root) return;
      if (e.target instanceof Node && root.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  function handleSelectAccount(a: SearchAccountResult) {
    setOpen(false);
    navigate(`/accounts/${a.id}/transactions`);
  }

  function handleSelectTransaction(t: SearchTransactionResult) {
    setOpen(false);
    // Clicking a transaction routes to its parent account's transactions
    // page — anchoring to the specific row would require deep-link support
    // we don't yet have on that page, and the scope explicitly accepts
    // this fallback as the simpler option that still feels right.
    navigate(`/accounts/${t.account_id}/transactions`);
  }

  const hasResults =
    results != null &&
    (results.accounts.length > 0 || results.transactions.length > 0);

  return (
    <div
      ref={containerRef}
      className="relative hidden max-w-sm flex-1 sm:block"
    >
      <Search
        aria-hidden
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        type="search"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (shouldQuery) setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder="Search accounts and transactions…"
        className="h-9 pl-8"
        aria-label="Search accounts and transactions"
        autoComplete="off"
      />

      {open && shouldQuery ? (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[28rem] overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md"
        >
          {loading && !results ? <ResultsSkeleton /> : null}
          {error ? (
            <p className="px-3 py-3 text-sm text-destructive">{error}</p>
          ) : null}
          {results != null && !error ? (
            hasResults ? (
              <SearchResultsList
                results={results}
                onSelectAccount={handleSelectAccount}
                onSelectTransaction={handleSelectTransaction}
              />
            ) : (
              <p className="px-3 py-3 text-sm text-muted-foreground">
                No results for &ldquo;{results.query}&rdquo;
              </p>
            )
          ) : null}
          {loading && results ? (
            <div className="flex items-center gap-2 border-t border-border px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              Updating…
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ResultsSkeleton() {
  return (
    <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      Searching…
    </div>
  );
}

interface SearchResultsListProps {
  results: SearchResponse;
  onSelectAccount: (a: SearchAccountResult) => void;
  onSelectTransaction: (t: SearchTransactionResult) => void;
}

function SearchResultsList({
  results,
  onSelectAccount,
  onSelectTransaction,
}: SearchResultsListProps) {
  return (
    <div className="py-1">
      {results.accounts.length > 0 ? (
        <ResultSection title="Accounts">
          {results.accounts.map((a) => (
            <AccountRow key={a.id} account={a} onSelect={onSelectAccount} />
          ))}
        </ResultSection>
      ) : null}
      {results.transactions.length > 0 ? (
        <ResultSection title="Transactions">
          {results.transactions.map((t) => (
            <TransactionRow
              key={t.id}
              transaction={t}
              onSelect={onSelectTransaction}
            />
          ))}
        </ResultSection>
      ) : null}
    </div>
  );
}

function ResultSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="px-1 py-1 first:pt-1 last:pb-1">
      <p className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      <ul>{children}</ul>
    </div>
  );
}

function AccountRow({
  account,
  onSelect,
}: {
  account: SearchAccountResult;
  onSelect: (a: SearchAccountResult) => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(account)}
        className="flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <span className="truncate font-medium text-foreground">
            {account.name}
          </span>
          {account.mask ? (
            <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
              •••• {account.mask}
            </span>
          ) : null}
        </span>
        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
          {formatBalance(account.balance, account.currency)}
        </span>
      </button>
    </li>
  );
}

function TransactionRow({
  transaction,
  onSelect,
}: {
  transaction: SearchTransactionResult;
  onSelect: (t: SearchTransactionResult) => void;
}) {
  const displayName = transaction.merchant_name?.trim() || transaction.name;
  // Sign convention from FIN-41 / FIN-43: Plaid's positive amount is an
  // outflow (spending — red) and negative is an inflow (income — green).
  // Invert for display so a purchase shows as a red negative and a refund
  // / deposit shows as a green positive, matching the per-account
  // transactions page.
  const displayed = -transaction.amount;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(transaction)}
        className="flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
      >
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium text-foreground">
            {displayName}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            {formatDate(transaction.date)}
          </span>
        </span>
        <span
          className={cn(
            "shrink-0 font-mono text-xs tabular-nums",
            displayed < 0 ? "text-destructive" : "text-success",
          )}
        >
          {formatAmount(displayed, transaction.currency)}
        </span>
      </button>
    </li>
  );
}

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

function formatAmount(value: number, currency: string | null): string {
  if (!currency || currency === "USD") return usdFormatter.format(value);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return usdFormatter.format(value);
  }
}

function formatBalance(value: number, currency: string): string {
  return formatAmount(value, currency);
}

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

function formatDate(iso: string): string {
  const ts = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(ts)) return iso;
  return dateFormatter.format(new Date(ts));
}
